import { useContext, useState, useEffect, useRef } from 'react';
import { useNavigate, useLocation, useParams } from 'react-router-dom';
import {
    Box,
    Paper,
    Typography,
    CircularProgress,
    Tabs,
    Tab,
    Container,
} from '@mui/material';
import {
    Payment as PaymentIcon,
    SportsTennis,
    VpnKey as VpnKeyIcon,
    InfoOutlined,
    Cancel as CancelIcon,
} from '@mui/icons-material';
import ShareOutlinedIcon from '@mui/icons-material/ShareOutlined';
import AddIcon from '@mui/icons-material/Add';
import { useTranslation } from 'react-i18next';
import BookingDialogPayments from '../../../components/dialogs/bookingDialog/BookingDialogPayments';
import BookingDialogDeviceAuths from '../../../components/dialogs/bookingDialog/BookingDialogDeviceAuths';
import { SttButton } from '../../../sporttia/buttons/SttButton';
import Details from '../../../components/public/game/Game/Details';
import PlayerList from '../../../components/public/game/Game/PlayerList';
import useGameService from '../../../services/GameService';
import { useShareGame } from '../../../lib/hooks';
import { Player } from '../../../types/models/Player';
import ShareGameDialog from '../../../components/public/game/Game/ShareGameDialog';
import { AppContext } from '../../../sporttia/AppContext';
import type { Error } from '../../../types/models/Error';
import {
    canAddMorePlayersToGame,
    cancelGameOptionToPaymentAction,
    wasCreatedWithFullRateForced,
} from '../../../lib/GameUtils';
import HandlePaymentsModal from '../groups/HandlePaymentsModal';
import {
    buildCancelBookingPaymentOptions,
    canDeleteBookingPayments,
} from '../../../components/dialogs/bookingDialog/cancelBookingPaymentOptions';
import { getErrorMessage } from '../../../lib/utils';
import { getPath } from '../../Pages';
import { canCancelGame, canManageGamePlayers } from './gamePermissions';

interface TabPanelProps {
    children?: React.ReactNode;
    value: number;
    index: number;
}

function TabPanel({ children, value, index }: TabPanelProps) {
    return value === index ? <Box>{children}</Box> : null;
}

type LocationState = {
    capacity?: number;
};

export default function GameSC() {
    const { id } = useParams<{ id: string }>();
    const { t } = useTranslation();
    const [tabValue, setTabValue] = useState(0);
    const [openShare, setOpenShare] = useState(false);
    const cxt = useContext(AppContext)!;

    const location = useLocation();
    const state = location.state as LocationState | undefined;
    const facilityCapacity = state?.capacity;

    const { useGetGame, useDeleteGamePlayers, useDeleteGame } =
        useGameService();
    const [players, setPlayers] = useState<Player[]>([]);
    const [deletingPlayerId, setDeletingPlayerId] = useState<number | null>(
        null,
    );
    const [openAskingPaymentProcedure, setOpenAskingPaymentProcedure] =
        useState(false);
    // Lock síncrono anti doble-envío: a diferencia de isPending (estado async),
    // un ref se actualiza de inmediato, así que bloquea un segundo clic aunque
    // ocurra antes del re-render.
    const cancellingRef = useRef(false);

    const navigate = useNavigate();

    const {
        data: gameResponse,
        status: gameStatus,
        isError,
    } = useGetGame(id ? parseInt(id) : 0, { enabled: !!id } as any);

    const game = gameResponse?.game;

    const isSportCenter = cxt.user?.role === 'SPORTCENTER';
    const isGameOwner = cxt.sc?.id === game?.facility?.sc?.id;
    const canManagePlayers = canManageGamePlayers(cxt);
    const canCancel = canCancelGame(cxt);

    const canDeletePlayer = () =>
        isSportCenter && isGameOwner && canManagePlayers;

    useEffect(() => {
        if (game && Array.isArray(game.players)) {
            setPlayers(game.players);
        }
    }, [game]);

    const deletePlayerMutation = useDeleteGamePlayers({
        onSuccess: (_: any, variables: any) => {
            setPlayers((prev: any) =>
                prev.filter((p: any) => p.id !== variables.id),
            );
            setDeletingPlayerId(null);
        },
        onError: (err: unknown) => {
            setDeletingPlayerId(null);
            const apiErr = err as Error;
            const errorMsg =
                apiErr?.error?.msg || t('Error al eliminar el jugador');
            cxt.showMessage('E', errorMsg);
        },
    });

    const handleDeletePlayer = (player: Player) => {
        setDeletingPlayerId(player.id);
        deletePlayerMutation.mutate({
            id: player.id,
            params: { cancelGameOnLastPlayer: true },
        });
    };

    // Cancelar el partido completo. Calca el flujo de cancelar reserva:
    // HandlePaymentsModal pregunta qué hacer con los pagos y el código elegido
    // viaja como `paymentAction` a DELETE /games/{id} (deleteBooking lo aplica).
    const deleteGameMutation = useDeleteGame({
        onSuccess: () => {
            setOpenAskingPaymentProcedure(false);
            cxt.showMessage('S', t('Deleted'));
            // El partido ya no existe: redirigir al cuadrante con replace para
            // que "atrás" no vuelva a la ficha borrada (provocaba un error al
            // recargar el partido inexistente).
            navigate(getPath('timetable'), { replace: true });
        },
        onError: (err: unknown) => {
            cancellingRef.current = false;
            setOpenAskingPaymentProcedure(false);
            cxt.showMessage('E', getErrorMessage(err));
        },
    });

    const cancelGame = (paymentAction: number) => {
        // El ref bloquea un segundo envío aunque el doble clic ocurra antes de
        // que React re-renderice con isPending=true / el modal cerrado.
        if (!game || cancellingRef.current) return;
        cancellingRef.current = true;
        deleteGameMutation.mutate({
            id: game.id,
            params: { paymentAction },
        });
    };

    const {
        snackbarOpen,
        setSnackbarOpen,
        copyLoading,
        handleCopyShareText,
        handleShareWhatsApp,
        handleShareTwitter,
    } = useShareGame(game);

    if (!id) return null;

    if (gameStatus === 'pending') {
        return (
            <Box
                display="flex"
                flexDirection="column"
                alignItems="center"
                justifyContent="center"
                style={{ minHeight: '100vh' }}
            >
                <CircularProgress />
            </Box>
        );
    }

    if (isError) {
        return (
            <Box
                display="flex"
                flexDirection="column"
                alignItems="center"
                justifyContent="center"
                style={{ minHeight: '100vh' }}
            >
                <Typography color="error" variant="h6">
                    No se pudo cargar el partido.
                </Typography>
            </Box>
        );
    }

    if (!game) {
        return null;
    }

    const maxPlayers = facilityCapacity ?? game?.facility?.schedule?.capacity;
    const slots: (Player | null)[] = Array(maxPlayers)
        .fill(null)
        .map((_, i) => players[i] || null);

    const creator = game.creator || null;
    const canAddMorePlayers = canAddMorePlayersToGame(game);

    return (
        <Container maxWidth="xl">
            <Paper>
                <Tabs
                    value={tabValue}
                    onChange={(_, v: number) => setTabValue(v)}
                    indicatorColor="primary"
                    textColor="primary"
                    style={{ marginBottom: 16 }}
                >
                    <Tab label={t('gameDetails')} icon={<SportsTennis />} />
                    {isSportCenter && isGameOwner && (
                        <Tab label={t('Payments')} icon={<PaymentIcon />} />
                    )}
                    {isSportCenter && isGameOwner && (
                        <Tab label={t('DeviceAuths')} icon={<VpnKeyIcon />} />
                    )}
                </Tabs>

                <TabPanel value={tabValue} index={0}>
                    <Box p={3}>
                        {/* Indicador de tarifa completa forzada */}
                        {wasCreatedWithFullRateForced(game) && (
                            <Box
                                style={{
                                    marginBottom: 24,
                                    padding: '12px 16px',
                                    backgroundColor: '#e3f2fd',
                                    borderRadius: 4,
                                    border: '1px solid #90caf9',
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: 8,
                                }}
                            >
                                <InfoOutlined
                                    style={{ color: '#1976d2', fontSize: 20 }}
                                />
                                <Box style={{ color: '#1565c0', fontSize: 14 }}>
                                    {t('game.createdWithFullRateWarning')}
                                </Box>
                            </Box>
                        )}

                        <Details game={game} />
                        <Box mt={4}>
                            <PlayerList
                                slots={slots}
                                onDeletePlayer={handleDeletePlayer}
                                canDeletePlayer={canDeletePlayer}
                                deletingPlayerId={deletingPlayerId}
                                creator={creator}
                            />
                        </Box>
                        {isSportCenter && isGameOwner && canManagePlayers && (
                            <Box mb={2} mt={3}>
                                <SttButton
                                    fullWidth
                                    variant="contained"
                                    color="primary"
                                    disabled={
                                        players.length >= maxPlayers ||
                                        !canAddMorePlayers
                                    }
                                    startIcon={<AddIcon />}
                                    onClick={() =>
                                        navigate(`/games/${game.id}/addPlayer`)
                                    }
                                >
                                    {t('AddPlayer')}
                                </SttButton>
                            </Box>
                        )}
                        <Box mb={2} mt={3}>
                            <SttButton
                                fullWidth
                                caption={t('shareMatch')}
                                onClick={() => setOpenShare(true)}
                                startIcon={<ShareOutlinedIcon />}
                                style={{ marginBottom: 12 }}
                            />
                        </Box>
                        {isSportCenter && isGameOwner && canCancel && (
                            <Box mb={2} mt={1}>
                                <SttButton
                                    fullWidth
                                    variant="contained"
                                    color="error"
                                    caption={t('cancelMatch')}
                                    startIcon={<CancelIcon />}
                                    loading={deleteGameMutation.isPending}
                                    onClick={() =>
                                        setOpenAskingPaymentProcedure(true)
                                    }
                                />
                            </Box>
                        )}
                    </Box>
                </TabPanel>

                {isSportCenter && isGameOwner && (
                    <TabPanel value={tabValue} index={1}>
                        <Box p={3}>
                            {game ? (
                                <BookingDialogPayments
                                    booking={game as any}
                                    onUpdate={() => {}}
                                />
                            ) : (
                                <Typography
                                    color="textSecondary"
                                    align="center"
                                >
                                    {t('app.game.noPaymentsForMatch')}
                                </Typography>
                            )}
                        </Box>
                    </TabPanel>
                )}

                {isSportCenter && isGameOwner && (
                    <TabPanel value={tabValue} index={2}>
                        <Box p={3}>
                            {game ? (
                                <BookingDialogDeviceAuths
                                    booking={game as any}
                                />
                            ) : (
                                <Typography
                                    color="textSecondary"
                                    align="center"
                                >
                                    {t('app.game.noDevicesForMatch')}
                                </Typography>
                            )}
                        </Box>
                    </TabPanel>
                )}
            </Paper>

            <ShareGameDialog
                open={openShare}
                onClose={() => setOpenShare(false)}
                translate={t}
                copyLoading={copyLoading}
                handleCopyShareText={handleCopyShareText}
                handleShareWhatsApp={handleShareWhatsApp}
                handleShareTwitter={handleShareTwitter}
                snackbarOpen={snackbarOpen}
                setSnackbarOpen={setSnackbarOpen}
            />

            {/* Modal preguntando qué hacer con los pagos al cancelar el partido */}
            <HandlePaymentsModal
                warningText={t('sc.games.delete.howToHandlePayments')}
                open={openAskingPaymentProcedure}
                onClose={() => setOpenAskingPaymentProcedure(false)}
                options={buildCancelBookingPaymentOptions(
                    canDeleteBookingPayments(cxt),
                    t,
                )}
                onAccept={(option: string | null) => {
                    // Guard anti doble-envío: si ya hay una cancelación en
                    // vuelo, ignorar nuevos "Aceptar".
                    if (option === null || deleteGameMutation.isPending) return;
                    const paymentAction =
                        cancelGameOptionToPaymentAction(option);
                    if (paymentAction === null) return;
                    // Cerrar el modal de inmediato: evita que se pueda volver a
                    // pulsar "Aceptar" mientras se borra el partido. El botón
                    // "Cancelar partido" queda en loading (isPending).
                    setOpenAskingPaymentProcedure(false);
                    cancelGame(paymentAction);
                }}
            />
        </Container>
    );
}
